import { useEffect, useMemo, useState } from 'react';
import {
  useWorkflowTemplate,
  useWorkflowTemplatesList,
} from 'src/modules/shared/hooks/api/templates/templates';
import { TCTemplateKind, type TCTemplateRecord } from 'src/api/generated';

type TemplateFilter = (template: TCTemplateRecord) => boolean;

type UseTemplateSelectorParams = {
  isOpen: boolean;
  templateFilter?: TemplateFilter;
};

export const useTemplateSelector = ({
  isOpen,
  templateFilter,
}: UseTemplateSelectorParams) => {
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useWorkflowTemplatesList({ kind: TCTemplateKind.WORKFLOW }, isOpen);

  const filteredTemplates = useMemo(
    () => (templateFilter ? templates.filter(templateFilter) : templates),
    [templates, templateFilter]
  );

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [workflowName, setWorkflowName] = useState<string>('');

  const { template: selectedTemplate, loading: templateLoading } =
    useWorkflowTemplate(selectedTemplateId, isOpen);

  useEffect(() => {
    const firstTemplateId = filteredTemplates[0]?.templateId;
    if (!selectedTemplateId && firstTemplateId) {
      setSelectedTemplateId(firstTemplateId);
    }
  }, [filteredTemplates, selectedTemplateId]);

  useEffect(() => {
    if (selectedTemplate) {
      setWorkflowName(selectedTemplate.name || '');
    }
  }, [selectedTemplate]);

  return {
    templates: filteredTemplates,
    templatesLoading,
    templatesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    selectedTemplateId,
    setSelectedTemplateId,
    selectedTemplate,
    templateLoading,
    workflowName,
    setWorkflowName,
  };
};
